import { Fragment } from 'react';

/**
 * Тепловая карта «строки × периоды». Общая для шкалы кала и прочих симптомов:
 * форма данных одинаковая — список строк и список столбцов со счётчиками.
 */

export const PERIODS = [
  { label: '2 мес', value: 2 },
  { label: '6 мес', value: 6 },
  { label: '12 мес', value: 12 },
];

// Подпись гранулярности столбца для выбранного периода.
export const GRANULARITY = {
  2: 'по неделям (пн–вс)',
  6: 'по 2 недели',
  12: 'по месяцам',
};

// 5 ступеней насыщенности: от нежно-зелёного до тёмно-зелёного.
export const GREEN_STEPS = ['#dcecd2', '#b4dca4', '#84c46f', '#4f9c41', '#2c6b26'];
export const EMPTY_COLOR = '#f4f1ec';

/**
 * Ступень насыщенности 1..5 для count > 0 при известном максимуме.
 * Малые максимумы раскладываются один-в-один (1 отметка = самый светлый),
 * дальше — корневая шкала: один выброс не должен обесцвечивать остальные клетки.
 */
export function intensityStep(count, maxCount) {
  if (count <= 0) return 0;
  if (count === 1) return 1; // одна отметка — всегда самый светлый оттенок
  const steps = GREEN_STEPS.length;
  if (maxCount <= steps) return Math.min(count, steps);
  return Math.min(Math.ceil(Math.sqrt(count / maxCount) * steps), steps);
}

export function cellStyle(count, maxCount) {
  const step = intensityStep(count, maxCount);
  if (step === 0) return { background: EMPTY_COLOR, color: 'transparent' };
  return {
    background: GREEN_STEPS[step - 1],
    // на тёмных ступенях белый текст читается, на светлых — тёмный
    color: step >= 4 ? '#ffffff' : '#2d4a22',
  };
}

export function pluralMarks(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'отметка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'отметки';
  return 'отметок';
}

/** Полоска «меньше → больше» под картой. */
export function DensityLegend() {
  return (
    <div className="ss-legend">
      <span className="ss-legend-caption">меньше</span>
      <span className="ss-legend-swatch" style={{ background: EMPTY_COLOR }} />
      {GREEN_STEPS.map(c => (
        <span key={c} className="ss-legend-swatch" style={{ background: c }} />
      ))}
      <span className="ss-legend-caption">больше</span>
    </div>
  );
}

/**
 * rows      — [{ key, label, title }]
 * columns   — [{ label, date_from, date_to, counts: [] }]
 * labelWidth — ширина колонки подписей строк, px
 */
export default function Heatmap({
  rows, columns, maxCount, activeCell, onCellClick, labelWidth = 24,
}) {
  const gridColumns = columns.length
    ? `${labelWidth}px repeat(${columns.length}, minmax(16px, 1fr))`
    : `${labelWidth}px`;

  return (
    <div className="ss-grid-scroll">
      <div className="ss-grid" style={{ gridTemplateColumns: gridColumns }}>
        {rows.map((row, rowIdx) => (
          <Fragment key={row.key}>
            <div className="ss-row-label" title={row.title || row.label}>
              {row.label}
            </div>
            {columns.map(col => {
              const count = col.counts[rowIdx] || 0;
              const isActive = activeCell
                && activeCell.row.key === row.key
                && activeCell.column.date_from === col.date_from;
              return (
                <button
                  key={col.date_from}
                  type="button"
                  className={`ss-cell${isActive ? ' ss-cell-active' : ''}`}
                  style={cellStyle(count, maxCount)}
                  onClick={() => onCellClick(row, col, count, col.details?.[rowIdx] || [])}
                  aria-label={`${row.title || row.label}, ${col.date_from} — ${col.date_to}: ${count}`}
                >
                  {count > 0 ? count : ''}
                </button>
              );
            })}
          </Fragment>
        ))}

        {/* Ось X: пустая ячейка под подписи строк + подписи столбцов.
            Подписи наклонены на 45°, иначе даты не помещаются по ширине клетки. */}
        <div className="ss-row-label" />
        {columns.map(col => (
          <div key={col.date_from} className="ss-col-label">
            <span>{col.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Карточка с подробностями выбранной клетки: что именно было отмечено. */
export function CellPopup({ cell, onClose, rowTitle }) {
  const details = cell.details || [];
  return (
    <div className="ss-popup">
      <div className="ss-popup-text">
        <div className="ss-popup-dates">
          {cell.column.date_from} — {cell.column.date_to}
        </div>
        <div className="ss-popup-name">{rowTitle}</div>

        {details.length === 0 ? (
          <div className="ss-popup-count">Отметок нет</div>
        ) : (
          <>
            {details.map(d => (
              <div key={d.name} className="ss-popup-fact">
                {d.name} — {d.count} {pluralMarks(d.count)}
              </div>
            ))}
            {details.length > 1 && (
              <div className="ss-popup-count">
                всего {cell.count} {pluralMarks(cell.count)}
              </div>
            )}
          </>
        )}
      </div>
      <button className="ss-popup-close" onClick={onClose}>✕</button>
    </div>
  );
}
